import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { epointsApi } from '../services/epointsApi';
import { depositsApi } from '../services/depositsApi';
import BalanceOverview from '../components/epoints/BalanceOverview';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon, 
  CalendarIcon,
  ChartBarIcon 
} from '@heroicons/react/24/outline';

const Balance: React.FC = () => {
  const { t } = useTranslation();
  const { dual } = useCurrency();
  const { data: balance, isLoading } = useQuery({
    queryKey: ['epoint-balance'],
    queryFn: epointsApi.getBalance,
  });

  // Get monthly stats from API
  const { data: monthlyStats, isLoading: monthlyStatsLoading } = useQuery({
    queryKey: ['monthly-stats'],
    queryFn: () => depositsApi.getMonthlyStats(),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-200 rounded-lg"></div>
              <div className="h-32 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary">
          {t('balance.title')}
        </h1>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <CalendarIcon className="h-4 w-4" />
          <span>{t('balance.lastTransaction', { date: monthlyStats?.lastTransactionDate ? new Date(monthlyStats.lastTransactionDate).toLocaleString() : (balance?.lastTransactionDate ? new Date(balance.lastTransactionDate).toLocaleString() : t('balance.noTransactionsYet')) })}</span>
        </div>
      </div>

      {/* Main Balance Display */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BalanceOverview balance={balance} />

        <div className="space-y-4">
          {/* Monthly Growth */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-secondary">
                {t('balance.monthlyGrowth')}
              </h3>
              <ChartBarIcon className="h-5 w-5 text-gray-400" />
            </div>
            
            {monthlyStatsLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ) : monthlyStats ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {monthlyStats?.lastTransactionDate ? 
                      t('balance.lastUpdated', { date: new Date(monthlyStats.lastTransactionDate).toLocaleDateString() }) : 
                      t('balance.thisMonth')
                    }
                  </span>
                  <div className="flex items-center space-x-2">
                    {monthlyStats?.growth?.amountGrowthPercent !== undefined ? (
                      <>
                        {monthlyStats.growth.amountGrowthPercent >= 0 ? (
                          <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`font-medium ${
                          monthlyStats.growth.amountGrowthPercent >= 0 ? 'text-primary-600' : 'text-red-600'
                        }`}>
                          {monthlyStats.growth.amountGrowthPercent >= 0 ? '+' : ''}{monthlyStats.growth.amountGrowthPercent}%
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">{t('balance.noDataAvailable')}</span>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">{t('balance.deposits')}</div>
                      <div className="font-medium text-secondary">
                        {monthlyStats?.currentMonth?.totalDeposits || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">{t('balance.thisMonth')}</div>
                      <div className="font-medium text-secondary">
                        +{(monthlyStats?.currentMonth?.totalEpoints || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                {t('balance.noMonthlyData')}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-secondary mb-4">
              {t('balance.quickStats')}
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {t('balance.conversionRate')}
                </span>
                <span className="font-medium text-secondary">
                  1€ = 100 ePoints
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {t('balance.accountStatus')}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {t('balance.active')}
                </span>
              </div>
              
              {balance && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {t('balance.utilizationRate')}
                  </span>
                  <span className="font-medium text-secondary">
                    {balance.totalPoints > 0 
                      ? ((balance.availablePoints / balance.totalPoints) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Balance Breakdown */}
      {balance && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-secondary">
              {t('balance.balanceBreakdown')}
            </h3>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">
                  {balance.totalPoints.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {t('balance.totalPointsEarned')}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t('balance.value', { amount: dual(balance.totalCashValue) })}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">
                  {balance.availablePoints.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {t('balance.availablePoints')}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t('balance.value', { amount: dual(balance.availableCashValue) })}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {balance.pendingPoints?.toLocaleString() || '0'}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {t('balance.pendingPoints')}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t('balance.processing')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Balance;